import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSyncConfigV1Service, serviceName } from '../service';
import { GetSourceDeploymentConfigIO, ServiceQueries } from '../types';
import { useSyncConfigV1Queries } from './use-sync-config-v1-queries';
import { UseSyncConfigV1QueryEndpointArgs, useSyncConfigV1Query } from './use-sync-config-v1-query';

/**
 * A hook that returns a query for the `GetSourceDeploymentConfig` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetSourceDeploymentConfigQuery = <E = unknown, D = GetSourceDeploymentConfigIO['output']>(
  args: UseSyncConfigV1QueryEndpointArgs<'GetSourceDeploymentConfig', E, D>
) =>
  useSyncConfigV1Query({
    endpointName: 'GetSourceDeploymentConfig',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetSourceDeploymentConfig` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetSourceDeploymentConfigQueries = <E = unknown, D = GetSourceDeploymentConfigIO['output']>(
  argsArr: UseSyncConfigV1QueryEndpointArgs<'GetSourceDeploymentConfig', E, D>[]
) =>
  useSyncConfigV1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetSourceDeploymentConfig',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetSourceDeploymentConfig` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetSourceDeploymentConfig = () => {
  const queryClient = useQueryClient();

  const fetchGetSourceDeploymentConfig = async <E = unknown, D = GetSourceDeploymentConfigIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSyncConfigV1QueryEndpointArgs<'GetSourceDeploymentConfig', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetSourceDeploymentConfigIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetSourceDeploymentConfig'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetSourceDeploymentConfigIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetSourceDeploymentConfig'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetSourceDeploymentConfig'>({
          serviceName,
          endpointName: 'GetSourceDeploymentConfig',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSyncConfigV1Service.GetSourceDeploymentConfig(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetSourceDeploymentConfig;
};
